import type { ChangeEventHandler } from "react";
import { useFormContext } from "react-hook-form";

import type { ApiMagentoConsumablesPackagingFormat } from "@jti/magento-types";
import { ConsumablesFormStepSection } from "@jti/ui/components/ConsumablesFormStepSection";
import { RadioCard } from "@jti/ui/components/RadioCard";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import type { ConsumablesFormValues } from "../../ConsumablesForm.types";
import { ConsumablesSectionTitle } from "../ConsumablesFormSectionTitle";
import { ConsumablesQuantityItemText } from "../ConsumablesQuantityItemText";

type QuantityGroupProps = {
  packagingFormat: ApiMagentoConsumablesPackagingFormat;
  groupName?: keyof ConsumablesFormValues;
  step: number;
  onChange: (quantity: string) => void;
};

export const ConsumablesQuantitySection = ({ groupName, packagingFormat, step, onChange }: QuantityGroupProps) => {
  const { __ } = useTranslations();
  const { register } = useFormContext<ConsumablesFormValues>();
  const quantities = Array.from({ length: packagingFormat.formatQuantity }, (_, index) => index + 1);

  const onQuantityChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const quantity = event.target.value;
    onChange(quantity);
  };

  const registerProps = register(groupName as string, { onChange: onQuantityChange });

  return (
    <ConsumablesFormStepSection
      title={
        <ConsumablesSectionTitle translationKey="consumablesForm.step.quantity_selection.title" stepCount={step} />
      }
      data-testid="quantity-selection-step"
    >
      {quantities.map((quantity) => (
        <RadioCard
          key={quantity}
          value={quantity}
          groupName={groupName as string}
          title={
            <ConsumablesQuantityItemText
              quantity={quantity}
              contentsType={packagingFormat.contentsType}
              itemsPerPackage={packagingFormat.itemsPerPackage}
              packagesPerBundle={packagingFormat.packagesPerBundle}
            />
          }
          {...registerProps}
        />
      ))}
    </ConsumablesFormStepSection>
  );
};
